import React from 'react'
import {
  QuickActionsRow,
  QuickActionButton,
  LayerItem,
  LayerLabel,
  OpacityControl,
  OpacityLabel,
  OpacitySlider,
  OpacityValue,
} from '../LayersPanel.styles'
import { LayerGroup, Layer } from '../LayersPanel.types'
import { ToggleSwitch } from '../../../atoms/ToggleSwitch'
import { Tooltip } from '../../../atoms/Tooltip'
import {
  DemographicsLegend,
  EmploymentLegend,
  PricingLegend,
  NoiseLegend,
  RestrictionsLegend,
  SunDirectionLegend,
} from '../../AnalysisMap/components'

export interface LayersPanelMultipleCheckboxGroupProps {
  group: LayerGroup
  isExpanded: boolean
  onLayerToggle: (layerId: string, enabled: boolean) => void
  onShowAll: () => void
  onHideAll: () => void
  onOpacityChange?: (layerId: string, opacity: number) => void
  isDemographicsLayer: (layerId: string) => boolean
  isEmploymentLayer: (layerId: string) => boolean
  isPricingLayer: (layerId: string) => boolean
  isNoiseLayer: (layerId: string) => boolean
  isSunDirectionLayer: (layerId: string) => boolean
}

export const LayersPanelMultipleCheckboxGroup: React.FC<
  LayersPanelMultipleCheckboxGroupProps
> = ({
  group,
  isExpanded,
  onLayerToggle,
  onShowAll,
  onHideAll,
  onOpacityChange,
  isDemographicsLayer,
  isEmploymentLayer,
  isPricingLayer,
  isNoiseLayer,
  isSunDirectionLayer,
}) => {
  // Calculate toggleable layers for quick actions
  const toggleableLayers = group.layers.filter(
    (l) => l.type !== 'radio' && !l.disabled
  )
  const showQuickActions =
    group.groupType === 'dynamic' && toggleableLayers.length > 1

  return (
    <>
      {/* Quick Actions (Show All / Hide All) */}
      {showQuickActions && isExpanded && (
        <QuickActionsRow>
          <QuickActionButton
            onClick={(e) => {
              e.stopPropagation()
              onShowAll()
            }}
          >
            Show All
          </QuickActionButton>
          <QuickActionButton
            onClick={(e) => {
              e.stopPropagation()
              onHideAll()
            }}
          >
            Hide All
          </QuickActionButton>
        </QuickActionsRow>
      )}

      {/* Layer Items with Toggles */}
      {group.layers.map((layer) => {
        const toggleSwitch = (
          <ToggleSwitch
            id={`layer-${layer.id}`}
            checked={layer.enabled}
            disabled={layer.disabled}
            onChange={(checked) => onLayerToggle(layer.id, checked)}
          />
        )

        return (
          <div key={layer.id}>
            <LayerItem>
              <LayerLabel htmlFor={`layer-${layer.id}`}>
                {layer.label}
              </LayerLabel>
              {/* Wrap with tooltip if disabled and has a reason */}
              {layer.disabled && layer.disabledReason ? (
                <Tooltip content={layer.disabledReason} placement="left">
                  {toggleSwitch}
                </Tooltip>
              ) : (
                toggleSwitch
              )}
            </LayerItem>

            {/* Show legend if layer is enabled and is a demographics layer */}
            {isDemographicsLayer(layer.id) && layer.enabled && (
              <div style={{ padding: '8px 0 0 0', marginLeft: '12px' }}>
                <DemographicsLegend layerId={layer.id} />
              </div>
            )}

            {/* Show legend if layer is enabled and is an employment layer */}
            {isEmploymentLayer(layer.id) && layer.enabled && (
              <div style={{ padding: '8px 0 0 0', marginLeft: '12px' }}>
                <EmploymentLegend layerId={layer.id} />
              </div>
            )}

            {/* Show legend if layer is enabled and is a pricing layer */}
            {isPricingLayer(layer.id) && layer.enabled && (
              <div style={{ padding: '8px 0 0 0', marginLeft: '12px' }}>
                <PricingLegend layerId={layer.id} />
              </div>
            )}

            {/* Show legend if layer is enabled and is a noise layer */}
            {isNoiseLayer(layer.id) && layer.enabled && (
              <div style={{ padding: '8px 0 0 0', marginLeft: '12px' }}>
                <NoiseLegend layerId={layer.id} />
              </div>
            )}

            {/* Show legend if layer is enabled and is a sun direction layer */}
            {isSunDirectionLayer(layer.id) && layer.enabled && (
              <div style={{ padding: '8px 0 0 0', marginLeft: '12px' }}>
                <SunDirectionLegend layerId={layer.id} />
              </div>
            )}

            {/* Show opacity control if layer has opacity and is enabled */}
            {layer.showOpacity && layer.enabled && onOpacityChange && (
              <OpacityControl>
                <OpacityLabel>Opacity</OpacityLabel>
                <OpacitySlider
                  type="range"
                  min="0"
                  max="100"
                  value={layer.opacity || 100}
                  onChange={(e) =>
                    onOpacityChange(layer.id, parseInt(e.target.value))
                  }
                />
                <OpacityValue>{layer.opacity || 100}%</OpacityValue>
              </OpacityControl>
            )}
          </div>
        )
      })}

      {/* Group-level Restrictions Legend */}
      {group.id === 'restrictions' && isExpanded && group.layers.length > 0 && (
        <div
          style={{
            padding: '12px 0 8px 12px',
            borderTop: '1px solid #e0e0e0',
            marginTop: '8px',
          }}
        >
          <RestrictionsLegend
            restrictionTypes={group.layers.map((layer) => ({
              id: layer.id.replace('restrictions-', ''),
              name: layer.label,
            }))}
          />
        </div>
      )}

      {/* Group-level Opacity Control */}
      {group.showGroupOpacity && isExpanded && onOpacityChange && (
        <OpacityControl
          style={{
            marginTop: group.id === 'restrictions' ? '0' : '8px',
            borderTop:
              group.id === 'restrictions' ? 'none' : '1px solid #e0e0e0',
            paddingTop: group.id === 'restrictions' ? '8px' : '12px',
            paddingLeft: group.id === 'restrictions' ? '12px' : '0',
          }}
        >
          <OpacityLabel>Opacity</OpacityLabel>
          <OpacitySlider
            type="range"
            min="0"
            max="100"
            value={group.groupOpacity || 30}
            onChange={(e) => {
              const newOpacity = parseInt(e.target.value)
              // Apply opacity to all layers in the group
              group.layers.forEach((layer) => {
                onOpacityChange(layer.id, newOpacity)
              })
            }}
          />
          <OpacityValue>{group.groupOpacity || 30}%</OpacityValue>
        </OpacityControl>
      )}
    </>
  )
}
